import React, { Component } from 'react';
import uuid from 'uuid';
import {
  Label, Card, Button, Input, Message, Icon,
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import util from 'utils/utils';

import { ImageChooser, VideoChooser, FileChooser } from 'components/lib/Choosers';
import Video from 'components/lib/Video/Video';
import RichText from 'components/lib/Editors/RichText';
import RichTextViewer from 'components/lib/Editors/RichTextViewer';

const StyledFreeformIdea = styled.div`
  min-height:500px;
  .field{
    position:relative;
    .field-toolbar{
      position:absolute;
      top: -35px;
      left: 0px;
      opacity:0.0;
      transition: opacity 0.25s;
    }
    &:hover{
      .field-toolbar{
        opacity:1.0;
      }
    }
  }
  .dropzone{
    height:30px;
     opacity:0.0;
    transition:opacity 0.25s;
    background:rgb(200,200,200);
    border: 3px dashed rgb(120,120,120);
    height:40px;
    margin: 10px 0px;
    position:relative;
    &:after{
      position:absolute;
      display:block;
      top:5px;
      font-size:30px;
      width:100%;
      text-align:center;
      content: '+';
      color: black;
    }
    &.picked{
      opacity:0.2;
    }
  }
  .gallery-image{
    width: 200px;
    height: 200px;
    margin: 3px 5px;
    display:inline-block;
    background-size: cover;
    background-position:center center;
    background-repeat:no-repeat;
    position:relative;
    .gallery-image-toolbar{
      opacity:0.0;
      position:absolute;
      padding-top:40%;
      top:0px;
      left:0px;
      width:100%;
      height:100%;
      background:rgba(250,250,250,0.5);
      text-align:center;
      transition:opacity 0.25s;
      &:hover{
        opacity:1.0;
      }
    }
  }
`;

class Freeform extends Component {
  constructor(props) {
    super(props);
    this.dragging = false;
    this.mouseOverCounter = 0;
    this.state = {};
  }

  dragEnter = (event) => {
    this.mouseOverCounter += 1;
    event.preventDefault();
    if (!this.dragging) {
      this.setState({ dragging: true });
      this.dragging = true;
    }
  };

  dragLeave = () => {
    this.mouseOverCounter -= 1;
    if (this.mouseOverCounter === 0) {
      if (this.dragging) {
        this.setState({ dragging: false });
        this.dragging = false;
      }
    }
  };

  dragOver = (event) => event.preventDefault();

  dragEnd = () => {
    this.setState({ dragging: false });
    this.dragging = false;
  };

  dragOverDropZone = (event) => event.preventDefault();

  dropDropZone = (event, fieldIndex) => {
    event.preventDefault();
    if (event.dataTransfer.items) {
      for (let i = 0; i < event.dataTransfer.items.length; i += 1) {
        const item = event.dataTransfer.items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file.type.toLowerCase().indexOf('image') === 0) {
            new ImageChooser({
              onComplete: (v) => this.addField({ type: 'image', value: v }, fieldIndex),
            }).uploadFile(file);
          } else {
            new FileChooser({
              forType: 'idea',
              forId: this.props.idea._id,
              onComplete: (f, n) => this.addField({ type: 'file', value: f, extra: { fileName: n } }, fieldIndex),
            }).uploadFile(file);
          }
        } else {
          this.addField({ type: event.dataTransfer.getData('key') }, fieldIndex);
        }
      }
    }
  };

  addField(newField, fieldIndex) {
    const fields = Object.assign([], this.props.idea.fields);
    if (!newField.extra) newField.extra = {};
    newField.extra._id = uuid.v4();
    if (Number.isNaN(fieldIndex)) fields.push(newField);
    else fields.splice(fieldIndex, 0, newField);
    this.props.updateIdea(fields, ['fields']);
    this.setState({ dragging: false });
    this.dragging = false;
  }

  deleteField = (fieldIndex) => {
    util.confirm('Delete this field?', 'This will remove this field and its contents from your idea').then(() => {
      const fields = Object.assign([], this.props.idea.fields);
      fields.splice(fieldIndex, 1);
      this.props.updateIdea(fields, ['fields']);
    }, () => { }).catch(() => {});
  };

  moveField = (fieldIndex, direction) => {
    const fields = Object.assign([], this.props.idea.fields);
    const field = fields[fieldIndex];
    fields.splice(fieldIndex, 1);
    if (direction === 'up') fields.splice(fieldIndex - 1, 0, field);
    else fields.splice(fieldIndex + 1, 0, field);
    this.props.updateIdea(fields, ['fields']);
  };

  addGalleryImage = (fieldIndex, image) => {
    const fields = Object.assign([], this.props.idea.fields);
    const field = fields[fieldIndex];
    if (!field.extra) field.extra = {};
    if (!field.extra.images) field.extra.images = [];
    field.extra.images.push(image);
    this.props.updateFreeformFieldExtra(fieldIndex, field.extra);
  };

  deleteGalleryImage = (fieldIndex, imageIndex) => {
    const fields = Object.assign([], this.props.idea.fields);
    const field = fields[fieldIndex];
    field.extra.images.splice(imageIndex, 1);
    this.props.updateFreeformFieldExtra(fieldIndex, field.extra);
  };

  moveGalleryImage = (fieldIndex, imageIndex, direction) => {
    const fields = Object.assign([], this.props.idea.fields);
    const field = fields[fieldIndex];
    const image = field.extra.images[imageIndex];
    field.extra.images.splice(imageIndex, 1);
    if (direction === 'up') field.extra.images.splice(imageIndex - 1, 0, image);
    else field.extra.images.splice(imageIndex + 1, 0, image);
    this.props.updateFreeformFieldExtra(fieldIndex, field.extra);
  };

  render() {
    const {
      idea, isEditing, updateFreeformField, updateFreeformFieldExtra, t,
    } = this.props;
    if (!idea) return null;
    return (
      <StyledFreeformIdea className={`${isEditing ? 'isEditing' : ''}`} onDragEnter={this.dragEnter} onDragLeave={this.dragLeave} onDragEnd={this.dragEnd} onDragOver={this.dragOver}>
        <div className={`dropzone ${this.state.dragging ? 'picked' : ''}`} onDrop={(e) => this.dropDropZone(e, 0)} onDragOver={this.dragOverDropZone} />
        {idea.fields && idea.fields.length > 0
          ? <>
            {idea.fields.map((field, fieldIndex) => <div key={(field.extra && field.extra._id) || fieldIndex} className='field'>
              {isEditing
                && <div className='field-toolbar'>
                  <Button.Group size='tiny' style={{ marginRight: 5 }}>
                    <Button size='tiny' color='red' icon='trash' content={t('ideas.freeform.actions.delete')} onClick={() => this.deleteField(fieldIndex)} />
                    {fieldIndex > 0
                      && <Button size='tiny' color='blue' icon='arrow up' content={t('ideas.freeform.actions.moveUp')} onClick={() => this.moveField(fieldIndex, 'up')} />
                    }
                    {fieldIndex < idea.fields.length - 1
                      && <Button size='tiny' color='blue' icon='arrow down' content={t('ideas.freeform.actions.moveDown')} onClick={() => this.moveField(fieldIndex, 'down')} />
                    }
                  </Button.Group>
                  {field.type === 'video' && field.value
                    && <VideoChooser
                      forType='idea' forId={idea._id}
                      trigger={<Button size='tiny' color='yellow' icon='film' content={t('ideas.freeform.actions.changeVideo')} />}
                      onComplete={(v) => updateFreeformField(fieldIndex, v)}
                    />
                  }
                  {field.type === 'image' && field.value
                    && <ImageChooser
                      trigger={<Button size='tiny' color='yellow' icon='film' content={t('ideas.freeform.actions.changeImage')} />}
                      onComplete={(v) => updateFreeformField(fieldIndex, v)}
                    />
                  }
                  {field.type === 'gallery'
                    && <ImageChooser
                      trigger={<Button size='tiny' color='yellow' icon='plus' content={t('ideas.freeform.actions.addImage')} />}
                      onComplete={(i) => this.addGalleryImage(fieldIndex, i)}
                    />
                  }
                  {field.type === 'file' && field.value
                    && <FileChooser
                      forType='idea' forId={idea._id}
                      onComplete={(f, n) => {
                        updateFreeformField(fieldIndex, f);
                        updateFreeformFieldExtra(fieldIndex, { fileName: n });
                      }}
                      trigger={<Button size='tiny' color='yellow' icon='file' content={t('ideas.freeform.file.change')} />}
                    />
                  }
                </div>
              }

              {field.type === 'title'
                && <div>
                  {isEditing
                    ? <Input size='large' fluid placeholder={t('ideas.freeform.actions.writeTitle')} onChange={(e) => updateFreeformField(fieldIndex, e.target.value)} value={field.value || ''} />
                    : <h2>{field.value}</h2>
                  }
                </div>
              }

              {field.type === 'text'
                && <div>
                  {isEditing
                    ? <RichText value={field.value || ''} onChange={(val) => updateFreeformField(fieldIndex, val)} />
                    : <RichTextViewer content={field.value} style={{ width: '80%', display: 'block', margin: '0px auto' }} />
                  }
                </div>
              }

              {field.type === 'image'
                && <div>
                  {isEditing && !field.value
                    && <div style={{ textAlign: 'center' }}>
                      <ImageChooser
                        onComplete={(i, n, src) => updateFreeformField(fieldIndex, src)}
                        trigger={<Button color='yellow' icon='image' content={t('ideas.actions.chooseImage')} />}
                      />
                    </div>
                  }
                  {field.value && <img alt='Freeform field' src={field.value} style={{ maxWidth: '100%', margin: '20px auto', display: 'block' }} />}
                </div>
              }

              {field.type === 'gallery'
                && <div>
                  {isEditing && (!field.extra || !field.extra.images || !field.extra.images.length)
                    && <div style={{ textAlign: 'center' }}>
                      <ImageChooser
                        trigger={<Button color='yellow' icon='plus' content={t('ideas.freeform.actions.galleryAddImage')} />}
                        onComplete={(i, n, src) => this.addGalleryImage(fieldIndex, src)}
                      />
                    </div>
                  }
                  <div style={{ textAlign: 'center' }}>
                    {field.extra && field.extra.images && field.extra.images.map((image, i) => <div key={i} className='gallery-image' style={{ backgroundImage: `url(${image})` }}>
                      {isEditing
                        && <div className='gallery-image-toolbar'>
                          <Button color='red' size='tiny' icon='trash' content='Delete' onClick={() => this.deleteGalleryImage(fieldIndex, i)} />
                          {i > 0
                            && <Button color='blue' size='tiny' icon='arrow left' onClick={() => this.moveGalleryImage(fieldIndex, i, 'up')} />
                          }
                          {i < field.extra.images.length - 1
                            && <Button color='blue' size='tiny' icon='arrow right' onClick={() => this.moveGalleryImage(fieldIndex, i, 'down')} />
                          }
                        </div>
                      }
                    </div>)}
                  </div>
                </div>
              }

              {field.type === 'video'
                && <div>
                  {isEditing && !field.value
                    && <div style={{ textAlign: 'center' }}>
                      <VideoChooser
                        forType='idea' forId={idea._id}
                        onComplete={(i) => updateFreeformField(fieldIndex, i)}
                        trigger={<Button color='yellow' icon='film' content={t('ideas.actions.chooseVideo')} />}
                      />
                    </div>
                  }
                  {field.value && <Video src={field.value} style={{ marginTop: '20px' }} forType='idea' forItem={idea} />}
                </div>
              }

              {field.type === 'file'
                && <div>
                  {isEditing && !field.value
                    && <div style={{ textAlign: 'center' }}>
                      <FileChooser
                        forType='idea' forId={idea._id}
                        onComplete={(f, n, src) => {
                          updateFreeformField(fieldIndex, src);
                          updateFreeformFieldExtra(fieldIndex, { fileName: n });
                        }}
                        trigger={<Button color='yellow' icon='file' content={t('ideas.actions.chooseFile')} />}
                      />
                    </div>
                  }
                  {field.value
                    && <Card.Group centered>
                      <Card raised>
                        <Card.Content textAlign='center'>
                          <Icon name='file' className='file-icon' style={{ fontSize: 30, color: 'rgb(200,200,200)', marginBottom: 10 }} />
                          <Card.Header style={{ overflowWrap: 'break-word' }}>{field.extra && field.extra.fileName}</Card.Header>
                          <Card.Meta>{t('ideas.freeform.file.attachment')}</Card.Meta>
                        </Card.Content>
                        <Card.Content extra>
                          <Button as='a' href={field.value} target='_blank' rel="noopener noreferrer" download={field.extra && field.extra.fileName} fluid color='blue' icon='download' content={t('ideas.freeform.file.download')} />
                        </Card.Content>
                      </Card>
                    </Card.Group>
                  }
                </div>
              }

              {field.type === 'url'
                && <div>
                  {isEditing
                    ? <Input fluid icon='globe' iconPosition='left' placeholder={t('ideas.freeform.url.placeholder')} value={field.value || ''} onChange={(e) => updateFreeformField(fieldIndex, util.ensureHttp(e.target.value))} />
                    : (field.value
                      && <div style={{ textAlign: 'center' }}>
                        <Label as='a' href={util.ensureHttp(field.value)} target='_blank' rel='noopener noreferrer' size='large'>
                          <Icon name='globe' /> {util.ensureHttp(field.value)}
                        </Label>
                      </div>
                    )
                  }
                </div>
              }

              <div className={`dropzone ${this.state.dragging ? 'picked' : ''}`} onDrop={(e) => this.dropDropZone(e, fieldIndex + 1)} onDragOver={this.dragOverDropZone} />
            </div>)
            }
          </>
          : <>
            {isEditing
              ? <div style={{
                minHeight: 200,
                background: 'white',
                boxShadow: '0px 10px 10px rgb(0,0,0,0.1)',
                textAlign: 'center',
                borderRadius: 5,
                fontSize: 20,
                padding: 40,
                border: '4px dashed rgb(230,230,230)',
              }}
                onDragOver={this.dragOverDropZone} onDrop={(e) => this.dropDropZone(e)}>
                <p><Icon name='plus' /></p>
                <p>{t('ideas.freeform.help.title')}</p>
                <p><small>{t('ideas.freeform.help.info')}</small></p>
              </div>
              : <Message style={{ marginTop: 60 }}>{t('ideas.empty')}</Message>
            }</>
        }
      </StyledFreeformIdea>
    );
  }
}

export default withTranslation()(Freeform);
